import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { getDashboard } from '../../services/ManagementService';
import FormTitle from '../../share-components/FormTitle';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
    PieChart, Pie, Cell, ResponsiveContainer, LabelList, Label,
    Legend
} from 'recharts';
import { Card, CardBody } from 'react-bootstrap';
import { SERVER_ERROR_MESSAGE } from '../../constants/commonConstants';

const Dashboard = () => {

    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [quantityData, setQuantityData] = useState([]);
    const [priceData, setPriceData] = useState([]);
    const [bestSoldProducts, setBestSoldProducts] = useState([]);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28CFF', '#FFEEAA'];

    const initialLoad = async () => {
        setLoading(true);
        try {
            const response = await getDashboard();
            setQuantityData(response?.quantityData || []);
            setPriceData(response?.priceData || []);
            setBestSoldProducts(response?.bestSoldProductData);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setRouteNaviData("MANAGEMENT >> DASHBOARD");
        initialLoad();
    }, []);

    return (
        <div id='dashboardPage'>
            <FormTitle title={"Dashboard"} isCenter={true} />
            {isFirstLoaded && <div style={{ paddingBottom: "50px"
                }}>
                <div style={{maxWidth: "750px", margin: '0 auto' }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px'
                    }}>
                        <Card>
                            <CardBody className='pt-2'>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexDirection: 'column'
                                }}>
                                    <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 20 }}>
                                        Top 5 Best-Selling Products <br />(Past 6 Months)
                                    </div>

                                    {/* Chart Container */}
                                    <div style={{ width: '100%', height: 250 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={bestSoldProducts}
                                                    dataKey="totalQuantity"
                                                    nameKey="productName"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={120}
                                                    fill="#8884d8"
                                                // label removed to show labels below
                                                >
                                                    {bestSoldProducts.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(value) => [`${value}`, 'Quantity']} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Custom Vertical Legend Below */}
                                    <div style={{
                                        // marginTop: 24,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: 10,
                                        padding: 20,
                                    }}>
                                        {bestSoldProducts.map((entry, index) => {
                                            const total = bestSoldProducts.reduce((sum, item) => sum + item.totalQuantity, 0);
                                            const percent = total > 0 ? ((entry.totalQuantity / total) * 100).toFixed(0) : 0;

                                            return (
                                                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div
                                                        style={{
                                                            width: 12,
                                                            height: 12,
                                                            backgroundColor: COLORS[index % COLORS.length],
                                                            borderRadius: '50%',
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                    <span style={{fontWeight: '500'}}>{entry.brandName.trim()} {entry.productName.trim()} ({percent}%)</span> 
                                                    {/* {entry.categoryName.trim()} */}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardBody className='pt-2'>
                                <div style={{ width: '100%', height: 500, paddingBottom: '60px' }}>
                                    <div style={{ textAlign: 'center', fontWeight: 'bold' }}>Monthly Purchase and Order Prices <br />(Past 6 Months)</div>
                                    <ResponsiveContainer>
                                        <BarChart
                                            data={priceData}
                                            margin={{ top: 20, right: 10, left: 45, bottom: 20 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="yearMonth"
                                                interval={0}
                                                angle={-30}
                                                textAnchor="end"
                                                height={60}
                                            />
                                            <YAxis label={{ value: 'Price', angle: -90, position: 'insideLeft', dx: -25 }} />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="totalPurchasedPrice" fill="#8884d8" name="Total Purchased Price" />
                                            <Bar dataKey="totalOrderedPrice" fill="#82ca9d" name="Total Ordered Price" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>


                        <Card>
                            <CardBody className='pt-2'>
                                <div style={{ width: '100%', height: 500, paddingBottom: '60px' }}>
                                    <div style={{ textAlign: 'center', fontWeight: 'bold' }}>Monthly Purchase and Order Quantities <br />(Past 6 Months)</div>
                                    <ResponsiveContainer>
                                        <BarChart
                                            data={quantityData}
                                            margin={{ top: 20, right: 10, left: 45, bottom: 20 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="yearMonth"
                                                interval={0}
                                                angle={-30}
                                                textAnchor="end"
                                                height={60}
                                            />
                                            <YAxis label={{ value: 'Quantity', angle: -90, position: 'insideLeft', dx: -25 }} />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="totalPurchasedQuantity" fill="#8884d8" name="Total Purchased Quantity" />
                                            <Bar dataKey="totalOrderedQuantity" fill="#82ca9d" name="Total Ordered Quantity" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>}
        </div>
    );
};

export default Dashboard;